import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import {
  canAccessAdminFeatures,
  canAccessDispatcherFeatures,
  canAccessTechnicianFeatures,
  useCompanyRole,
} from '../domains/auth/companyRole';
import type { CompanyRole } from '../domains/companies/types';
import { useAuthStore } from '../domains/auth/authStore';
import { getRoleIssueContent } from './roleIssueContent';
import { RoleFallbackScreen, RoleLoadingState } from './RoleStateScreen';

function canAccessAllowedRole(role: CompanyRole | null, allowed: CompanyRole[]) {
  return allowed.some((allowedRole) => {
    if (allowedRole === 'company_admin') return canAccessAdminFeatures(role);
    if (allowedRole === 'dispatcher') return canAccessDispatcherFeatures(role);
    return canAccessTechnicianFeatures(role);
  });
}

export function RoleGuard({ allowed, children }: { allowed: CompanyRole[]; children: ReactNode }) {
  const logout = useAuthStore((state) => state.logout);
  const { user, loading, isPlatformUser, companyRole, missingMembership, unknownRole, defaultRoute } =
    useCompanyRole();

  if (!user) return <Navigate to="/login" replace />;
  if (loading) return <RoleLoadingState label="Проверяем доступ к компании..." />;
  if (isPlatformUser) return <Navigate to="/superadmin" replace />;

  if (missingMembership) {
    const issue = getRoleIssueContent('missingMembership', 'guard');
    return (
      <RoleFallbackScreen
        icon={issue.icon}
        title={issue.title}
        text={issue.text}
        actionLabel="Выйти"
        onAction={() => logout()}
      />
    );
  }

  if (unknownRole || !companyRole) {
    const issue = getRoleIssueContent('unknownRole', 'guard');
    return (
      <RoleFallbackScreen
        icon={issue.icon}
        title={issue.title}
        text={issue.text}
        actionLabel="Выйти"
        onAction={() => logout()}
      />
    );
  }

  if (!canAccessAllowedRole(companyRole, allowed)) {
    return <Navigate to={defaultRoute} replace />;
  }

  return <>{children}</>;
}
